<template>
  <div
    class="card-container"
    :class="[`f-${theme}`, { 'is-pinned': pinned }]">
    <div class="card-header">
      <div class="card-header-icon">
        <svgicon :name="`patent/${patent.key}`" />
      </div>
      <div class="card-header-content">
        <div class="title-large">
          {{ $t(`data.patent.${patent.key}.name`) }}
          <help-button :page="`patent/${patent.key}`" />
        </div>
      </div>
      <div
        v-if="pinned"
        class="card-close"
        v-tooltip="$t('card.close_hint')"
        @click="$emit('close')">
        <svgicon name="close" />
      </div>
    </div>

    <div class="card-body">
      <div class="card-illustration">
        <img :src="`data/patents/${patent.illustration}`">
      </div>

      <div class="card-information">
        <div class="card-panel-controls">
          <svgicon
            class="card-panel-control"
            @click="movePanelToLeft()"
            name="caret-left"
            v-if="leftControl" />
          <div v-else></div>
          <svgicon
            class="card-panel-control"
            @click="movePanelToRight()"
            name="caret-right"
            v-if="rightControl" />
          <div v-else></div>
        </div>

        <div class="card-panel-window">
          <div
            ref="panelContainer"
            class="card-panel-container"
            :style="{ left: panelContainerPosition + 'px' }">
            <div class="card-panel">
              <blockquote>
                {{ $t(`data.patent.${patent.key}.quote`) }}
              </blockquote>

              <div
                v-for="unlock in patent.unlock"
                :key="`unlock-${patent.key}-${unlock.key}`"
                class="complex-bonus"
                @mouseover="showChild({type: unlock.type, key: unlock.key, level: unlock.level})"
                @mouseleave="hideChild()">
                <div v-if="unlock.type === 'building'">
                  <span v-html="$t('card.patent.unlocks_something', {something: $t(`data.building.${unlock.key}.name`)})" />
                  <span v-if="unlock.level > 1" v-html="$t('card.patent.level_hint', {lvl: unlock.level})" />
                </div>
                <div
                  v-if="unlock.type === 'ship'"
                  v-html="$t('card.patent.unlocks_something', {something: $t(`data.ship.${unlock.key}.name`)})">
                </div>
              </div>

              <div
                v-if="patent.info"
                class="complex-bonus">
                <div>{{ $t(`data.patent_info.${patent.info}`) }}</div>
              </div>
            </div>

            <div class="card-panel">
              <h2>{{ $t('card.patent.about') }}</h2>
              <p>{{ $t(`data.patent.${patent.key}.description`) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="card-action"
      v-if="!child">
      <div class="card-action-button">
        <div
          v-if="patent.status === 'locked'"
          class="button disabled">
          <div class="dashed">{{ $t('card.patent.locked') }}</div>
          <div class="dashed icon-value">
            {{ cost | integer }}
            <svgicon name="resource/technology" />
          </div>
        </div>
        <div
          v-if="patent.status === 'available'"
          class="button"
          @click="purchase(patent.key)">
          <div>{{ $t('card.patent.buy') }}</div>
          <div class="icon-value">
            {{ cost | integer }}
            <svgicon name="resource/technology" />
          </div>
        </div>
        <div
          v-if="patent.status === 'purchased'"
          class="button active"
          :class="`f-${theme}`">
          <div>{{ $t('card.patent.purchased') }}</div>
        </div>
      </div>
    </div>

    <div
      v-if="!child && activeChild !== null"
      class="card-child"
      @mouseenter="hoverCardEnter"
      @mouseleave="hoverCardLeave">
      <building-card
        v-if="activeChild.type === 'building'"
        :child="true"
        :buildingKey="activeChild.key"
        :level="activeChild.level"
        :theme="theme"
        :showCost="true"
        :pinned="cardPinned"
        @close="hoverCardClose" />
      <ship-card
        v-if="activeChild.type === 'ship'"
        :child="true"
        :shipKey="activeChild.key"
        :theme="theme"
        :showCost="true"
        :pinned="cardPinned"
        @close="hoverCardClose" />
    </div>
  </div>
</template>

<script>
import CardMixin from '@/game/mixins/CardMixin';
import HelpButton from '@/game/components/generic/HelpButton.vue';
import HoverCardMixin from '@/game/mixins/HoverCardMixin';
import BuildingCard from '@/game/components/card/BuildingCard.vue';
import ShipCard from '@/game/components/card/ShipCard.vue';

export default {
  name: 'patent-card',
  mixins: [CardMixin, HoverCardMixin],
  props: {
    patent: Object,
    costFactor: Number,
    pinned: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Same price the server charges: base × (1 + owned × increase) × the
    // Open Science / Lost Sciences multiplier (Player.purchase_patent/2).
    cost() {
      const multiplier = this.$store.state.game.instanceInfo.patent_cost_multiplier || 1;
      return this.patent.cost * (1 + (this.costFactor || 0)) * multiplier;
    },
  },
  methods: {
    purchase(patentkey) {
      this.$emit('purchase', patentkey);
    },
    // reroute CardMixin's plain show/hide through the hover-intent timers
    // so the child card can be reached and pinned like the system cards
    showChild(child) {
      this.hoverCardShow(child);
    },
    hideChild() {
      this.hoverCardHide();
    },
    hoverCardApply(payload) {
      this.activeChild = payload;
    },
    hoverCardVisible() {
      return this.activeChild !== null;
    },
  },
  components: {
    HelpButton,
    BuildingCard,
    ShipCard,
  },
};
</script>
